import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import api from "@/api/axios";
import useProjectFull from "@/hooks/useProjectFull";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Area,
  Legend,
  LabelList,
} from "recharts";
import { Trash2 } from "lucide-react";
import dashboardIcon from "@/assets/Frame.svg";
import settingsIcon from "@/assets/settings-6-line.svg";
import calenderIcon from "@/assets/ri-calendar-2-line.svg";
import downloadIcon from "@/assets/download.svg";
import archiveIcon from "@/assets/archive.svg";
import arrowRightIcon from "@/assets/arrowRight.svg";
import copyIcon from "@/assets/copyIcon.svg";
import TransactionViewModal from "./tabs/components/TransactionViewModal";
import SettingsDrawer from "./SettingsDrawer";

const maskId = (id) => (id ? `......${id.slice(-5)}` : "-");

const normalizeTransaction = (t) => ({
  ...t,
  paymentMethod:
    t.paymentMethod ||
    t.paymentMode ||
    t.paymentInfo?.paymentMethod ||
    t.paymentInfo?.mode ||
    t.paymentInfo?.method ||
    t.gatewayResponse?.payment_mode ||
    t.gatewayResponse?.method ||
    "-",
});

export default function ProjectDashboard() {
  const { id } = useParams();
  const { project, stats } = useProjectFull(id);

  useEffect(() => {
  if (!id) return;

  let cancelled = false;

  const verifyPendingCashfreeTxns = async () => {
    try {
      const res = await api.get(
        `/api/projects/${id}/transactions?status=created&gateway=cashfree&limit=20`
      );

      const pendingTxns = res.data?.data || [];

      for (const tx of pendingTxns) {
        if (cancelled) return;

        // fire-and-forget verification
        api.get(`/api/payments/verify/${tx.transactionId}`).catch(() => {});
      }
    } catch (err) {
      console.warn("[ProjectDashboard] verification skipped:", err?.message);
    }
  };

  verifyPendingCashfreeTxns();

  return () => {
    cancelled = true;
  };
}, [id]);


  useEffect(() => {
  }, [stats]);
  
  const [statsLoading, setStatsLoading] = useState(false);


  const [timeFilter, setTimeFilter] = useState("7d");
  const [statsDateRange, setStatsDateRange] = useState({ from: "", to: "" });
  const toDateInputRef = useRef(null);

  const handleStatsFromChange = (e) => {
    const val = e.target.value;
    const toWasEmpty = !statsDateRange.to;
    setStatsDateRange((p) => ({ ...p, from: val }));
    if (toWasEmpty && toDateInputRef.current) {
      setTimeout(() => {
        if (toDateInputRef.current.showPicker) {
          toDateInputRef.current.showPicker();
        } else {
          toDateInputRef.current.focus();
        }
      }, 0);
    }
  };

  const handleStatsToChange = (e) => {
    const val = e.target.value;
    setStatsDateRange((p) => ({ ...p, to: val }));
  };

  const [searchQuery, setSearchQuery] = useState("");
  const [txnDateRange, setTxnDateRange] = useState({ from: "", to: "" });

  const [filteredStats, setFilteredStats] = useState(null);
  const [filteredVolume, setFilteredVolume] = useState([]);
  const [gatewayStats, setGatewayStats] = useState([]);
  
  console.log('Current filteredStats:', JSON.stringify(filteredStats, null, 2));

  const [page, setPage] = useState(1);
  const limit = 12;
  const [selectedTxn, setSelectedTxn] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const [filters, setFilters] = useState({
    status: "all",
    gateway: "all",
  });
  const [transactions, setTransactions] = useState([]);
  const [hasFetched, setHasFetched] = useState(false);

  const fetchTransactions = useCallback(async (page = 1) => {
    try {
      const params = new URLSearchParams({
        page,
        limit: 20,
        ...(filters.status !== "all" && { status: filters.status }),
        ...(filters.gateway !== "all" && { gateway: filters.gateway }),
        ...(searchQuery.trim() && { search: searchQuery.trim() }),
        ...(txnDateRange.from && {
          startDate: new Date(txnDateRange.from).toISOString(),
        }),
        ...(txnDateRange.to && {
          endDate: new Date(
            new Date(txnDateRange.to).setHours(23, 59, 59, 999)
          ).toISOString(),
        }),
      });

      const res = await api.get(`/api/projects/${id}/transactions?${params.toString()}`);
      setTransactions((res.data.data || []).map(normalizeTransaction));
      setHasFetched(true);
    } catch (err) {
      console.error("Failed to load transactions", err);
      setHasFetched(true);
    }
  }, [id, filters, searchQuery, txnDateRange]);

  useEffect(() => {
    if (id) fetchTransactions(1);
  }, [fetchTransactions, id]);

  
const enabledGateways = useMemo(() => {
  return (gatewayStats || []).map(g => g.gateway.toLowerCase());
}, [gatewayStats]);


 
  useEffect(() => {
    const formatYMD = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

    const getDateRangeForFilter = () => {
      if (statsDateRange.from || statsDateRange.to) {
        return {
          from: statsDateRange.from || undefined,
          to: statsDateRange.to || undefined,
        };
      }

      const now = new Date();
      let from;
      let to = new Date(now);
      to.setHours(23, 59, 59, 999);

      if (timeFilter === "7d") {
        from = new Date(now);
        from.setDate(from.getDate() - 7);
        from.setHours(0, 0, 0, 0);
      }

      if (timeFilter === "month") {
        from = new Date(now.getFullYear(), now.getMonth(), 1);
        from.setHours(0, 0, 0, 0);
      }

      if (timeFilter === "lastMonth") {
        from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        from.setHours(0, 0, 0, 0);
        to = new Date(now.getFullYear(), now.getMonth(), 0);
        to.setHours(23, 59, 59, 999);
      }

      if (timeFilter === "year") {
        from = new Date(now.getFullYear(), 0, 1);
        from.setHours(0, 0, 0, 0);
        to = new Date(now.getFullYear(), 11, 31);
        to.setHours(23, 59, 59, 999);
      }

      return {
        from: from ? formatYMD(from) : undefined,
        to: formatYMD(to),
      };
    };

    const fetchFilteredData = async () => {
      if (!id) return;
       setStatsLoading(true);
      setFilteredStats(null);
      setGatewayStats([]);
      setFilteredVolume([]);



      try {
        const range = getDateRangeForFilter();
        
        console.log('fetchFilteredData called:', { 
          range, 
          timeFilter, 
          statsDateRange,
          enabledGatewaysCount: enabledGateways.length,
          enabledGateways: enabledGateways
        });

        const params = new URLSearchParams();
              if (range.from) {
          const [fy, fm, fd] = range.from.split("-").map(Number);
          const startDateISO = new Date(fy, fm - 1, fd).toISOString();
          params.append("startDate", startDateISO);
        }

        if (range.to) {
          const [ty, tm, td] = range.to.split("-").map(Number);
          const endDate = new Date(ty, tm - 1, td);
          endDate.setHours(23, 59, 59, 999);
          const endDateISO = endDate.toISOString();
          params.append("endDate", endDateISO);
        }


        setStatsLoading(true);

        const volumeRes = await api.get(
          `/api/projects/${id}/transactions?${params.toString()}&limit=1000`
        );

        const txns = volumeRes.data.data || [];

        
        const enabledTxns = txns.filter(tx => {
          const txGateway = (tx.gateway || "").toLowerCase();
          return enabledGateways.length === 0 || enabledGateways.includes(txGateway);
        });

        const byStatus = {};
        let totalAmount = 0;

        enabledTxns.forEach((tx) => {
          const status = tx.status || "created";
          if (!byStatus[status]) {
            byStatus[status] = { count: 0 };
          }
          byStatus[status].count += 1;
          if (status === "captured") {
            totalAmount += tx.amount || 0;
          }
        });

        setFilteredStats({
          totalAmount,
          byStatus,
        });
        
        console.log('Setting filteredStats:', { 
          totalAmount, 
          byStatus,
          txnsCount: enabledTxns.length,
          allTxnsCount: txns.length 
        });

       
        const gwMap = {};
        enabledTxns.forEach((tx) => {
          const gw = (tx.gateway || "unknown").toLowerCase();
          if (!gwMap[gw]) {
            gwMap[gw] = { total: 0, captured: 0 };
          }
          gwMap[gw].total += 1;
          if (tx.status === "captured") {
            gwMap[gw].captured += 1;
          }
        });

        const gwArray = Object.entries(gwMap).map(([gateway, counts]) => ({
          gateway,
          successRate: counts.total > 0 ? (counts.captured / counts.total) * 100 : 0,
          txnCount: counts.total,
        }));

        setGatewayStats(gwArray);

        const grouped = {};

        enabledTxns.forEach((tx) => {
          const d = new Date(tx.createdAt);
          const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
            2,
            "0"
          )}-${String(d.getDate()).padStart(2, "0")}`;

          if (!grouped[date]) {
            grouped[date] = {
              date,
              transactionCount: 0,
              totalAmount: 0,
            };
          }

          grouped[date].transactionCount += 1;

          if (tx.status === "captured") {
            grouped[date].totalAmount += tx.amount;
          }
        });

        const volumeData = Object.values(grouped).sort(
          (a, b) => new Date(a.date) - new Date(b.date)
        );

        setFilteredVolume(volumeData);
        setStatsLoading(false);
      } catch (err) {
        console.error("Failed to fetch filtered data:", err);
        setStatsLoading(false);
      }
    };

    fetchFilteredData();
   
  }, [id, timeFilter, statsDateRange.from, statsDateRange.to, project?.gatewayConfigs]);

  const handleInvoiceDownload = async (txnId) => {
    try {
      const res = await api.get(`/api/payments/transaction/${txnId}?format=pdf`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice-${txnId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Invoice download failed:", err);
    }
  };

  const totalPages = Math.ceil(transactions.length / limit);
  const paginatedTxns = transactions.slice((page - 1) * limit, page * limit);

  
const resolvedStats = useMemo(() => {
  if (!filteredStats) return null;

  const byStatus = filteredStats.byStatus || {};

  const captured = byStatus.captured?.count ?? 0;
  const failed = byStatus.failed?.count ?? 0;
  const created = byStatus.created?.count ?? 0;
  const processing = byStatus.processing?.count ?? 0;

  const totalTxns = captured + failed + created + processing;

  return {
    totalAmount: filteredStats.totalAmount ?? 0,
    captured,
    failed,
    created,
    processing,
    totalTxns,
    successRate:
      captured + failed > 0
        ? Number(((captured / (captured + failed)) * 100).toFixed(1))
        : 0,
  };
}, [filteredStats]);

 
  useEffect(() => {
    console.log('filteredStats CHANGED:', {
      timestamp: new Date().toISOString(),
      value: filteredStats
    });
  }, [filteredStats]);

  return (
    <div className="max-w-[1400px] mx-auto px-[10px] space-y-[30px] pb-10">
      
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <img src={dashboardIcon} alt="Dashboard" className="w-6 h-6" />
          <h1 className="text-[24px] font-semibold leading-[31.2px] text-black">
            {project?.name}
          </h1>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => (window.location.href = `/projects/${id}/test-payment`)}
            className="flex items-center gap-1 px-[10px] py-[8px] rounded-[20px] border border-[#C4C4C4] text-[12px] text-[#232323] font-normal hover:bg-[#F5F5F5] cursor-pointer transition-colors"
          >
            Test Payment
          </button>

          <button
            type="button"
            onClick={() => setSettingsOpen(true)}
            className="flex items-center gap-1 px-[10px] py-[8px] rounded-[20px] border border-[#C4C4C4] text-[12px] text-[#232323] font-normal hover:bg-[#F5F5F5] cursor-pointer transition-colors"
          >
            <img src={settingsIcon} alt="Settings" className="w-4 h-4" />
            Configure Gateway
          </button>
        </div>
      </div>

      
      <div className="flex justify-between items-center py-[5px]">
        <div className="flex gap-[6px]">
          {[
            { key: "7d", label: "Last 7 Days" },
            { key: "month", label: "This Month" },
            { key: "lastMonth", label: "Last Month" },
            { key: "year", label: "This Year" },
          ].map((item) => {
            const active = timeFilter === item.key;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => {
                  setTimeFilter(item.key);
                  setStatsDateRange({ from: "", to: "" });
                }}
                className={`px-[10px] py-[8px] rounded-[5px] text-[12px] leading-[15.6px] transition-colors cursor-pointer ${active ? "bg-[#D3E6FC] font-semibold" : "font-normal hover:bg-[#F5F5F5]"} text-[#232323]`}
              >
                {item.label}
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-[4px]">
          <div className="flex items-center gap-[4px] px-[10px] py-[8px] rounded-[5px]">
            <img src={calenderIcon} alt="Calendar" className="w-4 h-4" />
            <span className="text-[12px] text-[#232323] font-normal">Date Range</span>
          </div>
          <div className="min-w-[180px] flex items-center gap-[8px] px-[16px] py-[8px] bg-white rounded-[5px] border border-[#C4C4C4]">
            <input
              type="date"
              value={statsDateRange.from}
              onChange={handleStatsFromChange}
              className="text-[12px] font-medium text-[#232323] outline-none cursor-pointer"
            />
            <span className="text-[12px] text-[#757575]">to</span>
            <input
              ref={toDateInputRef}
              type="date"
              value={statsDateRange.to}
              onChange={handleStatsToChange}
              className="text-[12px] font-medium text-[#232323] outline-none cursor-pointer"
            />
          </div>
        </div>
      </div>

      {statsLoading ? (
        <StatsSkeleton />
      ) : (
        <StatsCards stats={resolvedStats} timeFilter={timeFilter} />
      )}



     {statsLoading ? (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 pb-4 animate-pulse">
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            className="h-[220px] rounded-[10px] bg-gray-100 border border-[#E6E6E6]"
          />
        ))}
      </div>
    ) : (
      <MidAnalytics
        stats={resolvedStats}
        project={project}
        gatewayStats={gatewayStats}
        enabledGateways={enabledGateways}
      />
    )}


      <TransactionVolumeChart
        data={filteredVolume}
        timeFilter={timeFilter}
        statsDateRange={statsDateRange}
      />

      <RecentTransactionsWithFilters
        transactions={paginatedTxns}
        page={page}
        totalPages={totalPages}
        setPage={setPage}
        filters={filters}
        setFilters={setFilters}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        dateRange={txnDateRange}
        setDateRange={setTxnDateRange}
        handleInvoiceDownload={handleInvoiceDownload}
        setSelectedTxn={setSelectedTxn}
        hasFetched={hasFetched}
      />

      {selectedTxn && (
        <TransactionViewModal txn={selectedTxn} onClose={() => setSelectedTxn(null)} />
      )}

      {settingsOpen && (
        <SettingsDrawer project={project} onClose={() => setSettingsOpen(false)} />
      )}
    </div>
  );
}

function StatsSkeleton() {
  return (
    <div className="flex gap-[20px] animate-pulse">
      {[...Array(4)].map((_, i) => (
        <div
          key={i}
          className="flex-1 bg-[#F1F1F1] rounded-[10px] border border-[#E6E6E6] px-[10px] py-[12px]"
        >
          <div className="h-3 w-24 bg-gray-200 rounded mb-2" />
          <div className="h-4 w-16 bg-gray-300 rounded" />
        </div>
      ))}
    </div>
  );
}


function StatsCards({ stats, timeFilter }) {
  if (!stats) return null;

  const getTimeLabel = () => {
    switch (timeFilter) {
      case "7d": return "Last 7 Days";
      case "month": return "This Month";
      case "lastMonth": return "Last Month";
      case "year": return "This Year";
      default: return "Selected Period";
    }
  };

  const cards = [
    {
      label: `Total TXs (${getTimeLabel()})`,
      value: `${stats.totalTxns.toLocaleString()} txns`,
    },
    {
      label: `Total Collected (${getTimeLabel()})`,
      value: `₹${stats.totalAmount.toLocaleString()}`,
    },
    {
      label: "Captured Transactions",
      value: `${stats.captured} txns`,
    },
    {
      label: "Failed Transactions",
      value: `${stats.failed} txns`,
    },
  ];

  return (
     <div className="flex gap-[20px]">
      {cards.map((card, i) => (
        <div
          key={i}
          className="flex-1 bg-[#F1F1F1] rounded-[10px] border border-[#E6E6E6] px-[10px] py-[6px]"
        >
          <div className="flex justify-between items-center">
            <div className="px-[6px] py-[3px]">
              <span className="text-[12px] font-normal text-[#232323] leading-[22px]">
                {card.label}
              </span>
            </div>
            <div className="px-[6px] py-[4px]">
              <span className="text-[12px] font-bold text-[#232323] leading-[22px] capitalize">
                {card.value}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function MidAnalytics({ stats, project, gatewayStats, enabledGateways }) {
  if (!stats) return null;
  const [copiedProjectId, setCopiedProjectId] = useState(false);

const copyProjectId = () => {
  if (!project?._id) return;
  navigator.clipboard.writeText(project._id);
  setCopiedProjectId(true);
  setTimeout(() => setCopiedProjectId(false), 2000);
};


 
  const filteredGatewayHealth = useMemo(() => {
    if (!enabledGateways || enabledGateways.length === 0) return [];
    
    const statsMap = new Map(
      (gatewayStats || []).map((g) => [g.gateway.toLowerCase(), g])
    );
    
    return enabledGateways.map((gw) => {
      const found = statsMap.get(gw);
      return {
        gateway: gw.charAt(0).toUpperCase() + gw.slice(1),
        successRate: found ? Number(found.successRate.toFixed(1)) : 0,
        txnCount: found ? found.txnCount : 0,
      };
    });
  }, [enabledGateways, gatewayStats]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 pb-4">
      
      <div className="self-stretch p-5 rounded-[10px] outline outline-1 outline-offset-[-1px] outline-[#E6E6E6] inline-flex flex-col justify-center items-start gap-2.5">
        <div className="py-[5px] inline-flex justify-center items-center gap-2.5">
          <div className="text-black text-sm font-semibold leading-[18.2px]">
            Payment Insights
          </div>
        </div>
        <div className="self-stretch h-0 outline outline-1 outline-offset-[-0.5px] outline-[#E6E6E6]" />
        <div className="self-stretch flex flex-col justify-start items-start gap-2.5">
          <div className="self-stretch py-1 inline-flex justify-between items-start">
            <div className="text-[#232323] text-xs font-medium leading-[15.6px]">
              Total Revenue
            </div>
            <div className="text-[#232323] text-xs font-normal capitalize leading-[15.6px]">
              ₹{stats.totalAmount.toLocaleString()}
            </div>
          </div>
          <div className="self-stretch py-1 inline-flex justify-between items-start">
            <div className="text-black text-xs font-medium leading-[15.6px]">
              Success Rate
            </div>
            <div className="text-[#232323] text-xs font-normal capitalize leading-[15.6px]">
              {stats.successRate}%
            </div>
          </div>
          <div className="self-stretch py-1 inline-flex justify-between items-start">
            <div className="text-black text-xs font-medium leading-[15.6px]">
              Captured Transactions
            </div>
            <div className="text-[#232323] text-xs font-normal capitalize leading-[15.6px]">
              {stats.captured}
            </div>
          </div>
          <div className="self-stretch py-1 inline-flex justify-between items-start">
            <div className="text-black text-xs font-medium leading-[15.6px]">
              Failed Transactions
            </div>
            <div className="text-[#232323] text-xs font-normal capitalize leading-[15.6px]">
              {stats.failed}
            </div>
          </div>
          <div className="self-stretch py-1 inline-flex justify-between items-start">
            <div className="text-black text-xs font-medium leading-[15.6px]">
              Created
            </div>
            <div className="text-[#232323] text-xs font-normal capitalize leading-[15.6px]">
              {stats.created}
            </div>
          </div>
          <div className="self-stretch py-1 inline-flex justify-between items-start">
            <div className="text-black text-xs font-medium leading-[15.6px]">
              Processing
            </div>
            <div className="text-[#232323] text-xs font-normal capitalize leading-[15.6px]">
              {stats.processing}
            </div>
          </div>
        </div>
      </div>

    
      <div className="self-stretch p-5 rounded-[10px] outline outline-1 outline-offset-[-1px] outline-[#E6E6E6] inline-flex flex-col justify-start items-start gap-2.5">
        <div className="py-[5px] inline-flex justify-center items-center gap-2.5">
          <div className="text-black text-sm font-semibold leading-[18.2px]">
            Gateway Health
          </div>
        </div>
        <div className="self-stretch h-0 outline outline-1 outline-offset-[-0.5px] outline-[#E6E6E6]" />
        <div className="self-stretch flex justify-start items-start gap-10 pt-1">
          <div className="flex-1 flex flex-col gap-5">
            <div className="text-[#232323] text-xs font-semibold leading-[15.6px]">Gateway</div>
            {filteredGatewayHealth.map((g) => (
              <div key={g.gateway} className="text-[#232323] text-xs font-normal leading-[15.6px]">
                {g.gateway}
              </div>
            ))}
            {filteredGatewayHealth.length === 0 && (
              <div className="text-[#232323] text-xs font-normal leading-[15.6px]">-</div>
            )}
          </div>
          <div className="flex-1 flex flex-col gap-5">
            <div className="text-[#232323] text-xs font-semibold leading-[15.6px]">Status</div>
            {filteredGatewayHealth.map((g) => {
              const rate = Number(g.successRate || 0);
              let label = "Down";
              let dot = "bg-red-500";
              if (rate >= 70) {
                label = "Live";
                dot = "bg-green-500";
              } else if (rate >= 30) {
                label = "Slow";
                dot = "bg-yellow-400";
              }
              return (
                <div
                  key={g.gateway}
                  className="flex items-center gap-2 text-[#232323] text-xs font-normal leading-[15.6px]"
                >
                  <span className={`w-2.5 h-2.5 rounded-full ${dot}`} />
                  {label}
                </div>
              );
            })}
            {filteredGatewayHealth.length === 0 && (
              <div className="text-[#232323] text-xs font-normal leading-[15.6px]">-</div>
            )}
          </div>
          <div className="flex-1 flex flex-col items-end gap-5">
            <div className="text-[#232323] text-xs font-semibold leading-[15.6px]">Success %</div>
            {filteredGatewayHealth.map((g) => (
              <div key={g.gateway} className="text-[#232323] text-xs font-normal leading-[15.6px]">
                {g.successRate.toFixed(1)}%
              </div>
            ))}
            {filteredGatewayHealth.length === 0 && (
              <div className="text-[#232323] text-xs font-normal leading-[15.6px]">-</div>
            )}
          </div>
          <div className="flex-1 flex flex-col items-end gap-5">
            <div className="text-[#232323] text-xs font-semibold leading-[15.6px]">Volume</div>
            {filteredGatewayHealth.map((g) => (
              <div key={g.gateway} className="text-[#232323] text-xs font-normal leading-[15.6px]">
                {g.txnCount}
              </div>
            ))}
            {filteredGatewayHealth.length === 0 && (
              <div className="text-[#232323] text-xs font-normal leading-[15.6px]">-</div>
            )}
          </div>
        </div>
      </div>

 
      <div className="self-stretch p-5 rounded-[10px] outline outline-1 outline-offset-[-1px] outline-[#E6E6E6] inline-flex flex-col justify-start items-start gap-2.5">
        <div className="py-[5px] inline-flex justify-center items-center gap-2.5">
          <div className="text-black text-sm font-semibold leading-[18.2px]">
            Business Details
          </div>
        </div>
        <div className="self-stretch h-0 outline outline-1 outline-offset-[-0.5px] outline-[#E6E6E6]" />
        <div className="self-stretch flex flex-col justify-start items-start gap-2.5">
          <div className="self-stretch py-1 inline-flex justify-between items-start">
            <div className="text-[#232323] text-xs font-medium leading-[15.6px]">
              Business Name
            </div>
            <div className="text-[#232323] text-xs font-normal capitalize leading-[15.6px]">
              {project?.name || "-"}
            </div>
          </div>
          <div className="self-stretch py-1 inline-flex justify-between items-start">
            <div className="text-black text-xs font-medium leading-[15.6px]">
              Active Gateways
            </div>
            <div className="text-[#232323] text-xs font-normal capitalize leading-[15.6px]">
              {enabledGateways?.length || 0}
            </div>
          </div>
          <div className="self-stretch py-1 inline-flex justify-between items-start">
            <div className="text-black text-xs font-medium leading-[15.6px]">
              Enabled Gateways
            </div>
            <div className="text-[#232323] text-xs font-normal capitalize leading-[15.6px]">
              {enabledGateways && enabledGateways.length
                ? enabledGateways.map(g => g.charAt(0).toUpperCase() + g.slice(1)).join(", ")
                : "-"}
            </div>
          </div>
          <div className="self-stretch py-1 inline-flex justify-between items-start">
  <div className="text-black text-xs font-medium leading-[15.6px]">
    Project ID
  </div>

  <div
    className="relative text-[#232323] text-xs font-normal leading-[15.6px] cursor-pointer flex items-center gap-1"
    onClick={copyProjectId}
  >
    {copiedProjectId ? (
      <span className="text-green-600">Copied</span>
    ) : (
      <>
        <span>{project?._id ? `......${project._id.slice(-5)}` : "-"}</span>
        <img src={copyIcon} alt="Copy" className="w-3 h-3 opacity-70" />
      </>
    )}
  </div>
</div>

        </div>
      </div>
    </div>
  );
}

const fillDateGaps = (data, fromDate, toDate) => {
  if (!fromDate || !toDate) return data || [];

  const [fy, fm, fd] = fromDate.split('-').map(Number);
  const [ty, tm, td] = toDate.split('-').map(Number);

  const start = new Date(fy, fm - 1, fd);
  const end = new Date(ty, tm - 1, td);

  const allDates = [];
  const current = new Date(start);

  while (current <= end) {
    const year = current.getFullYear();
    const month = String(current.getMonth() + 1).padStart(2, '0');
    const day = String(current.getDate()).padStart(2, '0');
    allDates.push(`${year}-${month}-${day}`);
    current.setDate(current.getDate() + 1);
  }

  const dataMap = new Map((data || []).map(item => [item.date, item]));

  return allDates.map(date => ({
    date,
    transactionCount: dataMap.get(date)?.transactionCount || 0,
    totalAmount: dataMap.get(date)?.totalAmount || 0
  }));
};

const formatDateLabel = (dateStr, isMonthView = false) => {
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  if (isMonthView) {
    const [year, month] = dateStr.split('-').map(Number);
    return `${months[month - 1]} ${year}`;
  }

  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const month = months[date.getMonth()];
  const day = String(date.getDate()).padStart(2, '0');
  return `${month} ${day}`;
};

const formatYMDFromDate = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

const aggregateByWeek = (data, fromDate) => {
  if (!data || !data.length || !fromDate) return [];

  const [fy, fm, fd] = fromDate.split('-').map(Number);
  const startDate = new Date(fy, fm - 1, fd);
  startDate.setHours(0, 0, 0, 0);

  const weekMap = new Map();

  data.forEach(item => {
    const [iy, im, id] = item.date.split('-').map(Number);
    const itemDate = new Date(iy, im - 1, id);
    itemDate.setHours(0, 0, 0, 0);

    const daysSinceStart = Math.floor((itemDate - startDate) / (1000 * 60 * 60 * 24));
    const weekNumber = Math.floor(daysSinceStart / 7);

    const weekStartDate = new Date(startDate);
    weekStartDate.setDate(startDate.getDate() + (weekNumber * 7));

    const year = weekStartDate.getFullYear();
    const month = String(weekStartDate.getMonth() + 1).padStart(2, '0');
    const day = String(weekStartDate.getDate()).padStart(2, '0');
    const weekKey = `${year}-${month}-${day}`;

    if (!weekMap.has(weekKey)) {
      weekMap.set(weekKey, {
        date: weekKey,
        transactionCount: 0,
        totalAmount: 0
      });
    }

    const existing = weekMap.get(weekKey);
    existing.transactionCount += item.transactionCount || 0;
    existing.totalAmount += item.totalAmount || 0;
  });

  return Array.from(weekMap.values()).sort((a, b) => a.date.localeCompare(b.date));
};

const aggregateByMonth = (data) => {
  if (!data || !data.length) return [];

  const monthMap = new Map();

  data.forEach(item => {
    const [iy, im] = item.date.split('-').map(Number);
    const monthKey = `${iy}-${String(im).padStart(2, '0')}`;

    if (!monthMap.has(monthKey)) {
      monthMap.set(monthKey, { date: monthKey, transactionCount: 0, totalAmount: 0 });
    }

    const existing = monthMap.get(monthKey);
    existing.transactionCount += item.transactionCount || 0;
    existing.totalAmount += item.totalAmount || 0;
  });

  return Array.from(monthMap.values()).sort((a, b) => a.date.localeCompare(b.date));
};

const fillMonthsBetween = (aggData, fromStr, toStr) => {
  const startParts = fromStr.split('-').map(Number);
  const endParts = toStr.split('-').map(Number);
  const start = new Date(startParts[0], startParts[1] - 1, 1);
  const end = new Date(endParts[0], endParts[1] - 1, 1);

  const map = new Map((aggData || []).map(item => [item.date, item]));
  const months = [];
  const cur = new Date(start);
  while (cur <= end) {
    const key = `${cur.getFullYear()}-${String(cur.getMonth()+1).padStart(2,'0')}`;
    const found = map.get(key);
    months.push({
      date: key,
      transactionCount: found ? found.transactionCount : 0,
      totalAmount: found ? found.totalAmount : 0
    });
    cur.setMonth(cur.getMonth() + 1);
  }
  return months;
};

const CustomBarLabel = (props) => {
  const { x, y, width, value } = props;
  return (
    <text
      x={x + width / 2}
      y={y - 5}
      fill="#232323"
      textAnchor="middle"
      fontSize={11}
      fontWeight="500"
    >
      {value}
    </text>
  );
};

function TransactionVolumeChart({ data, timeFilter, statsDateRange }) {
  const getRange = () => {
    if (statsDateRange?.from || statsDateRange?.to) {
      return {
        from: statsDateRange.from || undefined,
        to: statsDateRange.to || undefined,
      };
    }

    const now = new Date();
    let from;
    let to = new Date(now);
    to.setHours(23, 59, 59, 999);

    if (timeFilter === "7d") {
      from = new Date(now);
      from.setDate(from.getDate() - 7);
      from.setHours(0, 0, 0, 0);
    } else if (timeFilter === "month") {
      from = new Date(now.getFullYear(), now.getMonth(), 1);
      from.setHours(0, 0, 0, 0);
    } else if (timeFilter === "lastMonth") {
      from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      from.setHours(0, 0, 0, 0);
      to = new Date(now.getFullYear(), now.getMonth(), 0);
      to.setHours(23, 59, 59, 999);
    } else if (timeFilter === "year") {
      from = new Date(now.getFullYear(), 0, 1);
      from.setHours(0, 0, 0, 0);
      to = new Date(now.getFullYear(), 11, 31);
      to.setHours(23, 59, 59, 999);
    }

    return {
      from: from ? formatYMDFromDate(from) : undefined,
      to: formatYMDFromDate(to),
    };
  };

  const { from, to } = getRange();

  const [processedData, setProcessedData] = useState([]);
  const [viewMode, setViewMode] = useState("daily");

  useEffect(() => {
    if (!data || data.length === 0 || !from || !to) {
      setProcessedData([]);
      return;
    }

    const [fromYear, fromMonth, fromDay] = from.split("-").map(Number);
    const [toYear, toMonth, toDay] = to.split("-").map(Number);

    const start = new Date(fromYear, fromMonth - 1, fromDay);
    const end = new Date(toYear, toMonth - 1, toDay);

    const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;

    if (timeFilter === "year") {
      setViewMode("monthly");
      const agg = aggregateByMonth(data);
      setProcessedData(fillMonthsBetween(agg, from, to));
      return;
    }

    if (daysDiff <= 31) {
      setViewMode("daily");
      setProcessedData(fillDateGaps(data, from, to));
    } else if (daysDiff < 122) {
      setViewMode("weekly");
      setProcessedData(aggregateByWeek(data, from));
    } else {
      setViewMode("monthly");
      const agg = aggregateByMonth(data);

      const fillMonthsBetween = (aggData, fromStr, toStr) => {
        const start = new Date(fromStr + 'T00:00:00');
        const end = new Date(toStr + 'T00:00:00');
        start.setDate(1);
        start.setHours(0,0,0,0);
        end.setDate(1);
        end.setHours(0,0,0,0);

        const map = new Map((aggData || []).map(item => [item.date, item]));
        const months = [];
        const cur = new Date(start);
        while (cur <= end) {
          const key = `${cur.getFullYear()}-${String(cur.getMonth()+1).padStart(2,'0')}`;
          const found = map.get(key);
          months.push({
            date: key,
            transactionCount: found ? found.transactionCount : 0,
            totalAmount: found ? found.totalAmount : 0
          });
          cur.setMonth(cur.getMonth() + 1);
        }
        return months;
      };

      setProcessedData(fillMonthsBetween(agg, from, to));
    }
  }, [data, from, to, timeFilter]);

  const chartConfig = useMemo(() => {
    if (!processedData || processedData.length === 0) {
      return { barSize: 40, interval: 0, width: "100%" };
    }

    const len = processedData.length;
    let barSize = 40;
    let interval = 0;
    let width = "100%";

    if (len <= 5) barSize = 60;
    else if (len <= 7) barSize = 50;
    else if (len <= 10) barSize = 40;
    else if (len <= 15) barSize = 30;
    else if (len <= 31) barSize = 18;
    else barSize = 12;

    if (viewMode === 'daily') {
  if (len <= 10) interval = 0;
  else if (len <= 20) interval = 1;
  else if (len <= 31) interval = 2;
  else interval = 3;

 
  if (len > 31) {
    width = len * 40;
  } else {
    width = '100%';
  }
}

    return { barSize, interval, width };
  }, [processedData, viewMode]);

  const yAxisDomains = useMemo(() => {
    if (!processedData || processedData.length === 0) {
      return { left: [0, 100], right: [0, 10] };
    }

    const maxAmount = Math.max(...processedData.map((d) => d.totalAmount || 0));
    const maxCount = Math.max(...processedData.map((d) => d.transactionCount || 0));

    return {
      left: [0, Math.ceil(maxAmount * 1.2) || 100],
      right: [0, Math.ceil(maxCount * 1.2) || 10],
    };
  }, [processedData]);

  const formatXAxisLabel = useCallback(
    (dateStr) => {
      if (viewMode === "monthly") {
        return formatDateLabel(dateStr, true);
      } else if (viewMode === "weekly") {
        const [iy, im, id] = dateStr.split('-').map(Number);
        const date = new Date(iy, im - 1, id);
        const month = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][date.getMonth()];
        const day = String(date.getDate()).padStart(2, "0");
        return `${month} ${day}`;
      } else {
        return formatDateLabel(dateStr, false);
      }
    },
    [viewMode]
  );

  const chartTitle = useMemo(() => {
    if (viewMode === "monthly") return "Transaction Volume (Monthly)";
    if (viewMode === "weekly") return "Transaction Volume (Weekly)";
    return "Transaction Volume (Daily)";
  }, [viewMode]);

  if (!processedData || processedData.length === 0) {
    return null;
  }

  return (
    <div className="rounded-[10px] mt-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-[15px] font-semibold text-black leading-[18.2px]">
          {chartTitle}
        </h2>
      </div>

      <div className="w-full overflow-x-auto">
        <div
          className="flex justify-start"
          style={{
            width: typeof chartConfig.width === "number" ? Math.min(chartConfig.width, 1400) : chartConfig.width,
            maxWidth: "1400px",
            height: "320px",
          }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={processedData} margin={{ top: 30, right: 60, left: 60, bottom: 20 }}>
              <defs>
                <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2563eb" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#2563eb" stopOpacity={0.05} />
                </linearGradient>
              </defs>

              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E6E6E6" />

              <XAxis dataKey="date" tickFormatter={formatXAxisLabel} tick={{ fontSize: 11, fill: '#757575' }} axisLine={{ stroke: '#E6E6E6' }} tickLine={false} interval={chartConfig.interval} />

              <YAxis
                yAxisId="left"
                orientation="left"
                domain={yAxisDomains.left}
                tickFormatter={(v) => {
                  if (v >= 1000000) return `₹${(v / 1000000).toFixed(1)}M`;
                  if (v >= 1000) return `₹${(v / 1000).toFixed(0)}K`;
                  return `₹${v}`;
                }}
                tick={{ fontSize: 11, fill: "#757575" }}
                axisLine={{ stroke: "#E6E6E6" }}
                tickLine={false}
                label={{
                  value: 'Amount Collected',
                  angle: -90,
                  position: 'insideLeft',
                  style: { fontSize: 11, fill: '#232323', fontWeight: '500' }
                }}
              />

              <YAxis yAxisId="right" orientation="right" domain={yAxisDomains.right} tick={{ fontSize: 11, fill: "#757575" }} axisLine={{ stroke: "#E6E6E6" }} tickLine={false} label={{ value: 'Transaction Count', angle: 90, position: 'insideRight', style: { fontSize: 11, fill: '#232323', fontWeight: '500' } }} />

              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #E6E6E6',
                  borderRadius: '8px',
                  fontSize: '12px'
                }}
                formatter={(value, name) => {
                  if (name === 'Amount Collected') {
                    return [`₹${value.toLocaleString()}`, name];
                  }
                  if (name === 'Transactions') {
                    return [value, name];
                  }
                  return null;
                }}
                labelFormatter={(label) => {
                  if (viewMode === 'weekly') {
                    return `Week of ${formatXAxisLabel(label)}`;
                  }
                  if (viewMode === 'monthly') {
                    return formatXAxisLabel(label);
                  }
                  return formatXAxisLabel(label);
                }}
              />

              <Legend verticalAlign="top" align="center" height={36} iconType="rect" wrapperStyle={{ paddingBottom: '10px' }} />

              <Bar yAxisId="right" dataKey="transactionCount" name="Transactions" fill="#16a34a" radius={[6, 6, 0, 0]} barSize={chartConfig.barSize}>
                {processedData.length <= 31 && <LabelList content={CustomBarLabel} />}
              </Bar>

              <Area yAxisId="left" type="monotone" dataKey="totalAmount" fill="url(#colorAmount)" stroke="none" legendType="none" />

              <Line yAxisId="left" type="monotone" dataKey="totalAmount" name="Amount Collected" stroke="#2563eb" strokeWidth={2} dot={processedData.length <= 31 ? { r: 4, fill: "#ffffff", stroke: "#2563eb", strokeWidth: 2 } : false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function RecentTransactionsWithFilters({
  transactions,
  page,
  totalPages,
  setPage,
  filters,
  setFilters,
  searchQuery,
  setSearchQuery,
  dateRange,
  setDateRange,
  handleInvoiceDownload,
  setSelectedTxn,
  hasFetched,
}) {
  const [copied, setCopied] = useState({ value: null, type: null });
  const [hoveredCell, setHoveredCell] = useState(null);
  const toDateInputRef = useRef(null);

  const copyToClipboard = (value, type) => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    setCopied({ value, type });
    setTimeout(() => setCopied({ value: null, type: null }), 2000);
  };

  const handleTxnFromChange = (e) => {
    const val = e.target.value;
    const toWasEmpty = !dateRange.to;
    setDateRange((p) => ({ ...p, from: val }));
    if (toWasEmpty && toDateInputRef.current) {
      setTimeout(() => {
        if (toDateInputRef.current.showPicker) {
          toDateInputRef.current.showPicker();
        } else {
          toDateInputRef.current.focus();
        }
      }, 0);
    }
  };

  const getStatusStyles = (status) => {
    switch (status) {
      case "captured":
        return "text-green-700 bg-green-50 border border-green-600";
      case "failed":
        return "text-red-600 bg-red-50 border border-red-500";
      case "processing":
        return "text-orange-600 bg-orange-50 border border-orange-500";
      case "created":
      default:
        return "text-gray-600 bg-gray-50 border border-gray-400";
    }
  };

  const getVisiblePages = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1, 2);
      if (page > 4) pages.push("...");
      for (
        let i = Math.max(3, page - 1);
        i <= Math.min(totalPages - 2, page + 1);
        i++
      ) {
        pages.push(i);
      }
      if (page < totalPages - 3) pages.push("...");
      pages.push(totalPages - 1, totalPages);
    }
    return pages;
  };

  return (
    <div className="mt-6">
      <h2 className="text-[15px] font-semibold text-black mb-3">Recent Transactions</h2>

      
      <div className="flex flex-wrap gap-3 mb-4">
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          className="px-[10px] py-[8px] rounded-[20px] border border-[#C4C4C4] text-[12px]"
        >
          <option value="all">All Status</option>
          <option value="created">Created</option>
          <option value="processing">Processing</option>
          <option value="captured">Captured</option>
          <option value="failed">Failed</option>
        </select>

        <select
          value={filters.gateway}
          onChange={(e) => setFilters({ ...filters, gateway: e.target.value })}
          className="px-[10px] py-[8px] rounded-[20px] border border-[#C4C4C4] text-[12px] text-[#232323] font-normal hover:bg-[#F5F5F5] cursor-pointer transition-colors"
        >
          <option value="all">All Gateways</option>
          <option value="payu">PayU</option>
          <option value="razorpay">Razorpay</option>
          <option value="cashfree">Cashfree</option>
          <option value="paytm">PayTM</option>
          <option value="paypal">PayPal</option>
        </select>

      <input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search txn id or amount"
          className="px-[10px] py-[8px] rounded-[20px] border border-[#C4C4C4] text-[12px] text-[#232323] font-normal flex-1 min-w-0 hover:bg-[#F5F5F5] transition-colors"
        />

        <button
          onClick={() => {
            const headers = ["Transaction ID","Gateway ID","Payment Method","Gateway Used","Status","Date","Amount"];
            const rows = transactions.map((t) => [
              t.transactionId,
              t.gatewayPaymentId || "",
              t.paymentMethod || "-",
              t.gateway,
              t.status,
              new Date(t.createdAt).toLocaleString("en-IN"),
              t.amount,
            ]);
            const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
            const blob = new Blob([csv], { type: "text/csv" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "transactions.csv";
            a.click();
            URL.revokeObjectURL(url);
          }}
          className="px-[10px] py-[8px] rounded-[20px] border border-[#C4C4C4] text-[12px] text-[#232323] font-normal hover:bg-[#F5F5F5] transition-colors whitespace-nowrap"
        >
          ↓ CSV
        </button>

        <input
          type="date"

          value={dateRange.from}
          onChange={handleTxnFromChange}
          className="px-[10px] py-[8px] rounded-[20px] border border-[#C4C4C4] text-[12px] text-[#232323] font-normal cursor-pointer hover:bg-[#F5F5F5] transition-colors"
        />

        <input
          ref={toDateInputRef}
          type="date"
          value={dateRange.to}
          onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
          className="px-[10px] py-[8px] rounded-[20px] border border-[#C4C4C4] text-[12px] text-[#232323] font-normal cursor-pointer hover:bg-[#F5F5F5] transition-colors"
        />
      </div>

   
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-b border-[#EDEDED]">
              <th className="px-3 py-2 text-xs font-semibold">Transaction ID</th>
              <th className="px-3 py-2 text-xs font-semibold">Gateway ID</th>
              <th className="px-3 py-2 text-xs font-semibold">Payment Method</th>
              <th className="px-3 py-2 text-xs font-semibold">Gateway Used</th>
              <th className="px-3 py-2 text-xs font-semibold">Status</th>
              <th className="px-3 py-2 text-xs font-semibold">Date</th>
              <th className="px-3 py-2 text-xs font-semibold">Amount</th>
              <th className="px-3 py-2 text-xs font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {!hasFetched && (
              [...Array(6)].map((_, i) => (
                <tr key={`skeleton-${i}`} className="animate-pulse">
                  {Array.from({ length: 8 }).map((_, j) => (
                    <td key={j} className="px-3 py-3">
                      <div className={`h-3 bg-gray-200 rounded ${j === 7 ? 'w-16 ml-auto' : j === 4 ? 'w-16' : 'w-full'}`} />
                    </td>
                  ))}
                </tr>
              ))
            )}

            {hasFetched && transactions.length === 0 && (
              <tr>
                <td colSpan={8} className="py-8 text-center text-gray-500">
                  No transactions to display.
                </td>
              </tr>
            )}

            {hasFetched && transactions.map((tx) => (
              <tr
                key={tx._id}
                className="border-b border-[#F0F0F0] hover:bg-[#E8E8E8] cursor-pointer transition-colors duration-150"
                onClick={(e) => {
                  if (!e.target.closest('.action-button') && !e.target.closest('.copy-cell')) {
                    setSelectedTxn(tx);
                  }
                }}
              >
                <td
                  className="px-3 py-2 text-xs cursor-pointer relative copy-cell"
                  onMouseEnter={() => setHoveredCell(`txn-${tx._id}`)}
                  onMouseLeave={() => setHoveredCell(null)}
                  onClick={(e) => {
                    e.stopPropagation();
                    copyToClipboard(tx.transactionId, "txn");
                  }}
                >
                  {hoveredCell === `txn-${tx._id}` ? (
                    <div className="flex items-center gap-1">
                      <img src={copyIcon} className="w-4 h-4" alt="Copy" />
                      <span>Copy</span>
                    </div>
                  ) : (
                    maskId(tx.transactionId)
                  )}
                </td>
                <td
                  className="px-3 py-2 text-xs cursor-pointer relative copy-cell"
                  onMouseEnter={() => setHoveredCell(`gateway-${tx._id}`)}
                  onMouseLeave={() => setHoveredCell(null)}
                  onClick={(e) => {
                    e.stopPropagation();
                    copyToClipboard(tx.gatewayPaymentId, "gateway");
                  }}
                >
                  {hoveredCell === `gateway-${tx._id}` ? (
                    <div className="flex items-center gap-1">
                      <img src={copyIcon} className="w-4 h-4" alt="Copy" />
                      <span>Copy</span>
                    </div>
                  ) : (
                    maskId(tx.gatewayPaymentId)
                  )}
                </td>
                <td className="px-3 py-2 text-xs capitalize">{tx.paymentMethod || "-"}</td>
                <td className="px-3 py-2 text-xs capitalize">{tx.gateway}</td>
                <td className="px-3 py-2">
                  <span
                    className={`px-2 py-0.5 rounded text-[11px] font-medium ${getStatusStyles(tx.status)}`}
                  >
                    {tx.status ? tx.status.charAt(0).toUpperCase() + tx.status.slice(1) : "-"}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs text-gray-500">
                  {new Date(tx.createdAt).toLocaleString("en-IN")}
                </td>
                <td className="px-3 py-2 text-xs font-semibold">
                  ₹{tx.amount.toLocaleString()}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="flex justify-end gap-4">
                    <img
                      src={downloadIcon}
                      className="w-4 cursor-pointer hover:opacity-80 transition-opacity action-button"
                      title="Download Invoice"
                      onClick={(e) => { e.stopPropagation(); handleInvoiceDownload(tx._id); }}
                      alt="download"
                    />
                    <img
                      src={archiveIcon}
                      className="w-4 cursor-pointer hover:opacity-80 transition-opacity action-button"
                      title="Archive"
                      onClick={(e) => { e.stopPropagation(); }}
                      alt="archive"
                    />
                    <img
                      src={arrowRightIcon}
                      className="w-auto cursor-pointer hover:opacity-80 transition-opacity hover:scale-105 transition-transform action-button"
                      title="View"
                      onClick={(e) => { e.stopPropagation(); setSelectedTxn(tx); }}
                      alt="view"
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {copied.value && (
        <div className="fixed bottom-10 left-1/2 transform -translate-x-1/2 z-50 animate-fade-in-up">
          <div className="bg-[#232323] text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2">
            <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-sm font-medium">
              {copied.type === 'txn' ? 'Transaction ID' : 'Gateway ID'} copied to clipboard
            </span>
          </div>
        </div>
      )}

     
      <div className="flex justify-center mt-10">
        <div className="flex items-center gap-2 bg-[#F5F5F5] px-3 py-1.5 rounded-full">
          <button
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            className="px-2 text-gray-500 disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
          >
            ‹
          </button>
          {getVisiblePages().map((p, idx) =>
            p === "..." ? (
              <span key={idx} className="px-1 text-gray-500">
                …
              </span>
            ) : (
              <button
                key={p}
                onClick={() => setPage(p)}
                className={`w-7 h-7 text-xs rounded-full cursor-pointer transition-colors ${
                  page === p
                    ? "bg-purple-600 text-white shadow-md"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                {p}
              </button>
            )
          )}
          <button
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
            className="px-2 text-gray-500 disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
          >
            ›
          </button>
        </div>
      </div>
    </div>
  );
}

